import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from "react";
import * as HeicTo from "heic-to";
import { Loader } from "@progress/kendo-react-indicators";
import { useLocalization } from "@progress/kendo-react-intl";
import { TileLayout } from "@progress/kendo-react-layout";
import { useWindowSize } from 'usehooks-ts';
import { enMessages } from "../../../../messages/en-US";
import { Groups, Endpoints, Gateways, GreasePump } from "../../../../Models/Assets/AssetTemplateReadDto";
import GroupsTableTemplateComponent from "../Tables/GroupsTableTemplateComponent";
import EndpointsTableTemplateComponent from "../Tables/EndpointsTableTemplateComponent";
import GatewaysTableTemplateComponent from "../Tables/GatewaysTableTemplateComponent";
import AssetImageComponent from "../../../Assets/Components/Images/AssetImageComponent";
import { ImageUploadButton } from "./ImageUploadButton";
import { resizeImageToMaxSize, base64BlobLimit } from "./imageResizeUtils";
import { ExternalDropZone, Upload } from "@progress/kendo-react-upload";
import type { UploadFileInfo } from "@progress/kendo-react-upload";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ImageDimensions = { width: number; height: number };

interface Props {
  formRenderProps?: any;
  groups: Groups[];
  setGroups: Dispatch<SetStateAction<Groups[]>>;
  endpoints: Endpoints[];
  setEndpoints: Dispatch<SetStateAction<Endpoints[]>>;
  gateways: Gateways[];
  setGateways: Dispatch<SetStateAction<Gateways[]>>;
  greasePumps?: GreasePump[];
  setGreasePumps?: Dispatch<SetStateAction<GreasePump[]>>;
  selectedGroupId: string | null;
  setSelectedGroupId: Dispatch<SetStateAction<string | null>>;
  isEditMode?: boolean;
  imageBase64: string | null;
  setImageBase64: Dispatch<SetStateAction<string | null>>;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function toImageDataUrl(base64: string | null): string | null {
  if (!base64 || typeof base64 !== 'string' || base64.length === 0) return null;
  if (!base64.startsWith('data:image')) {
    let mime = 'image/png';
    if (base64.startsWith('/9j/')) mime = 'image/jpeg';
    else {
      try {
        const decoded = atob(base64.slice(0, 80));
        if (decoded.trimStart().startsWith('<svg') || decoded.trimStart().startsWith('<?xml')) {
          mime = 'image/svg+xml';
        }
      } catch {
        /* ignore */
      }
    }
    return `data:${mime};base64,${base64}`;
  }
  return base64;
}

function scalePosition(value: number, scale: number): number {
  return Math.round(value * scale);
}

function scaleGroups(groups: Groups[], scaleX: number, scaleY: number): Groups[] {
  return groups.map(g => ({
    ...g,
    xPos: scalePosition(g.xPos, scaleX),
    yPos: scalePosition(g.yPos, scaleY),
  }));
}

function scaleGateways(gateways: Gateways[], scaleX: number, scaleY: number): Gateways[] {
  return gateways.map(gw => ({
    ...gw,
    xPos: scalePosition(gw.xPos ?? 0, scaleX),
    yPos: scalePosition(gw.yPos ?? 0, scaleY),
  }));
}

function scaleGreasePumps(pumps: GreasePump[], scaleX: number, scaleY: number): GreasePump[] {
  return pumps.map(p => ({
    ...p,
    xPos: scalePosition(p.xPos ?? 0, scaleX),
    yPos: scalePosition(p.yPos ?? 0, scaleY),
  }));
}

/** Default positions for first group/gateway to avoid overlap when resetting (e.g. on image upload) */
const DEFAULT_GROUP_POS = { x: 50, y: 50 };
const DEFAULT_GATEWAY_POS = { x: 120, y: 50 };

function resetPositionsToZero<T extends { xPos: number; yPos?: number }>(
  items: T[],
  type?: 'group' | 'gateway'
): T[] {
  if (type === 'group' && items.length > 0) {
    return items.map((item, i) =>
      i === 0 ? { ...item, xPos: DEFAULT_GROUP_POS.x, yPos: DEFAULT_GROUP_POS.y } : { ...item, xPos: 0, yPos: 0 }
    );
  }
  if (type === 'gateway' && items.length > 0) {
    return items.map((item, i) =>
      i === 0 ? { ...item, xPos: DEFAULT_GATEWAY_POS.x, yPos: DEFAULT_GATEWAY_POS.y } : { ...item, xPos: 0, yPos: 0 }
    );
  }
  return items.map(item => ({ ...item, xPos: 0, yPos: 0 }));
}

function getNextGroupId(groups: Groups[]): string {
  const activeGroups = groups.filter(
    g => g.status === 'Active' || (g.status === undefined && g.isEnabled !== false)
  );
  const numericIds = activeGroups
    .map(g => parseInt(g.id, 10))
    .filter(n => !Number.isNaN(n) && n >= 1);
  return String(numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1);
}

function getNextGatewayId(gateways: Gateways[]): string {
  if (gateways.length === 0) return '1';
  const numericIds = gateways
    .filter(g => g.status !== 'Deleted')
    .map(g => parseInt(g.gatewayId, 10))
    .filter(n => !Number.isNaN(n) && n >= 1);
  return String(numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1);
}

/** Returns a name unique among active groups (case-insensitive). */
function getUniqueGroupName(baseName: string, groups: Groups[]): string {
  const active = groups.filter((g) => g.status === 'Active' || (g.status === undefined && g.isEnabled !== false));
  const used = new Set(active.map((g) => (g.name || '').trim().toLowerCase()));
  let candidate = baseName.trim();
  if (!used.has(candidate.toLowerCase())) return candidate;
  for (let n = 2; n < 1000; n++) {
    candidate = `${baseName} (${n})`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  return `${baseName} (${Date.now()})`;
}

function createNewGroup(id: string, existingGroups: Groups[]): Groups {
  const baseName = `Group ${id}`;
  const uniqueName = getUniqueGroupName(baseName, existingGroups);
  return {
    id,
    name: uniqueName,
    parentId: '',
    imageId: '',
    xPos: 0,
    yPos: 0,
    isEnabled: true,
    status: 'Active',
    attachmentImageIds: [],
  };
}

/** Returns a name unique among active endpoints (case-insensitive). */
function getUniqueEndpointName(baseName: string, endpoints: Endpoints[]): string {
  const active = endpoints.filter(
    (e) => e.status === 'Active' || (e.status === undefined && e.isEnabled !== false)
  );
  const used = new Set(active.map((e) => (e.name || '').trim().toLowerCase()));
  let candidate = baseName.trim();
  if (!used.has(candidate.toLowerCase())) return candidate;
  for (let n = 2; n < 1000; n++) {
    candidate = `${baseName} (${n})`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  return `${baseName} (${Date.now()})`;
}

function createNewEndpoint(
  groupId: string,
  pointId: number,
  existingEndpoints: Endpoints[]
): Endpoints {
  const baseName = `Endpoint ${groupId}.${pointId}`;
  const uniqueName = getUniqueEndpointName(baseName, existingEndpoints);
  return {
    id: `${groupId}-${pointId}`,
    groupId,
    greasePumpId: '',
    name: uniqueName,
    pointId,
    endpointDeviceId: '',
    endpointDeviceSerialNumber: '',
    requirementId: '',
    requirementName: '',
    xPos: 0,
    yPos: 0,
    isEnabled: true,
    status: 'Active',
  };
}

/** Returns a name unique among active gateways (case-insensitive). */
function getUniqueGatewayName(baseName: string, gateways: Gateways[]): string {
  const active = gateways.filter((g) => g.status === 'Active' || g.status === undefined);
  const used = new Set(active.map((g) => (g.name || '').trim().toLowerCase()));
  let candidate = baseName.trim();
  if (!used.has(candidate.toLowerCase())) return candidate;
  for (let n = 2; n < 1000; n++) {
    candidate = `${baseName} (${n})`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  return `${baseName} (${Date.now()})`;
}

function createNewGateway(gatewayId: string, existingGateways: Gateways[]): Gateways {
  const baseName = `Gateway ${gatewayId}`;
  const uniqueName = getUniqueGatewayName(baseName, existingGateways);
  return {
    id: '',
    gatewayId,
    name: uniqueName,
    xPos: 0,
    yPos: 0,
    status: 'Active',
  };
}

function mergeMarkersIntoGroups(
  groups: Groups[],
  markers: Array<{ id: string; position: { xPos: number; yPos: number } }>
): Groups[] {
  return groups.map(group => {
    const marker = markers.find(m => m.id === group.id);
    if (marker) {
      return { ...group, xPos: marker.position.xPos, yPos: marker.position.yPos };
    }
    return group;
  });
}

function getGatewayKey(gw: Gateways): string {
  return gw.id || `temp-gw-${gw.gatewayId}`;
}

function mergeMarkersIntoGateways(
  gateways: Gateways[],
  markers: Array<{ id: string; position: { xPos: number; yPos: number } }>
): Gateways[] {
  return gateways.map(gw => {
    const key = getGatewayKey(gw);
    const marker = markers.find(m => m.id === `gateway_${key}`);
    if (marker) {
      return { ...gw, xPos: marker.position.xPos, yPos: marker.position.yPos };
    }
    return gw;
  });
}

function createAssetData(
  templateName: string,
  groups: Groups[],
  endpoints: Endpoints[],
  gateways: Gateways[]
) {
  return {
    id: '',
    name: templateName,
    description: '',
    imageId: '',
    isGreasePumpConnectivityEnabled: false,
    endpoints,
    gateways,
    groups,
  };
}

/** Same approach as AssetConfigurationComponent: use rowSpan for single-column stacking; slightly larger tiles in Configuration */
function getColumnsDisplayPosition(isSingleColumn: boolean) {
  return isSingleColumn
    ? [
        { col: 1, colSpan: 1, rowSpan: 40 },
        { col: 1, colSpan: 1, rowSpan: 40 },
      ]
    : [
        { col: 1, colSpan: 1, rowSpan: 50 },
        { col: 2, colSpan: 1, rowSpan: 50 },
      ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AssetTemplateConfigurationComponent(props: Props): ReactElement {
  const localizationService = useLocalization();
  const { height, width } = useWindowSize();
  const containerHeight = height - 300;
  const isSingleColumn = width < 768;
  const rowHeight = 25; // Fixed like AssetConfigurationComponent for proper single-column stacking

  // Use props instead of local state
  const { groups, setGroups, endpoints, setEndpoints, gateways, setGateways, setGreasePumps, selectedGroupId, setSelectedGroupId, isEditMode, setImageBase64, formRenderProps } = props;
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isGroupImageEditing, setIsGroupImageEditing] = useState<boolean>(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageVersion, setImageVersion] = useState(0);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const imageDimensionsRef = useRef<{ width: number; height: number } | null>(null);
  imageDimensionsRef.current = imageDimensions;

  // Use image from parent state (Dialog) - this persists across steps
  const currentImageValue = props.imageBase64;
  
  const imageUrl = useMemo(
    () => toImageDataUrl(currentImageValue),
    [currentImageValue]
  );

  // Load image dimensions when image is set from parent (e.g. loading template for edit)
  useEffect(() => {
    if (!imageUrl) {
      setImageDimensions(null);
      return;
    }
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };
  }, [imageUrl]);

  const prevImageRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (prevImageRef.current === undefined) {
      prevImageRef.current = currentImageValue;
      return;
    }
    if (prevImageRef.current !== currentImageValue) {
      setImageVersion(v => v + 1);
    }
    prevImageRef.current = currentImageValue;
  }, [currentImageValue]);

  // Get template name from form
  const templateName = formRenderProps?.valueGetter('name') || '';

  // Handle group selection
  const handleGroupSelect = (groupId: string | null) => {
    setSelectedGroupId(groupId);
  };

  // Handle groups table change
  const handleGroupsTableChange = (updatedGroups: Groups[]) => {
    setGroups(updatedGroups);
  };

  const handleMarkersChange = (markers: Array<{ id: string; position: { xPos: number; yPos: number } }>) => {
    setGroups(mergeMarkersIntoGroups(groups, markers));
  };

  const handleGatewayMarkersChange = (markers: Array<{ id: string; position: { xPos: number; yPos: number } }>) => {
    setGateways(mergeMarkersIntoGateways(gateways, markers));
  };

  const handleAddGroup = () => {
    const newId = getNextGroupId(groups);
    const newGroup = createNewGroup(newId, groups);
    setGroups(prev => [...prev, newGroup]);
    setSelectedGroupId(newId);

    // Automatically create 1 endpoint for the new group
    const newEndpoint = createNewEndpoint(newId, 1, endpoints);
    setEndpoints(prev => [...prev, newEndpoint]);
  };

  const handleAddEndpoint = () => {
    if (!selectedGroupId) return;
    const groupEndpoints = endpoints.filter(
      e => e.groupId === selectedGroupId && (e.status === 'Active' || (e.status === undefined && e.isEnabled !== false))
    );
    const nextPointId =
      groupEndpoints.length === 0
        ? 1
        : Math.max(...groupEndpoints.map(e => e.pointId ?? 0), 0) + 1;
    const newEndpoint = createNewEndpoint(
      selectedGroupId,
      nextPointId,
      endpoints
    );
    setEndpoints(prev => [...prev, newEndpoint]);
  };

  const handleAddGateway = () => {
    const newGatewayId = getNextGatewayId(gateways);
    setGateways(prev => [...prev, createNewGateway(newGatewayId, prev)]);
  };

  const handleGatewaysChange = (updatedGateways: Gateways[]) => {
    setGateways(updatedGateways);
  };

  const handleImageChange = useCallback((base64: string | null, newDimensions?: ImageDimensions) => {
    const oldDims = imageDimensionsRef.current;
    if (oldDims && newDimensions && base64) {
      const scaleX = newDimensions.width / oldDims.width;
      const scaleY = newDimensions.height / oldDims.height;
      setGroups(prev => scaleGroups(prev, scaleX, scaleY));
      setGateways(prev => scaleGateways(prev, scaleX, scaleY));
      setGreasePumps?.(prev => scaleGreasePumps(prev, scaleX, scaleY));
    } else if (base64) {
      setGroups(prev => resetPositionsToZero(prev, 'group'));
      setGateways(prev => resetPositionsToZero(prev, 'gateway'));
      setGreasePumps?.(prev => resetPositionsToZero(prev));
    }
    setImageBase64(base64);
    if (typeof formRenderProps?.valueSetter === 'function') {
      formRenderProps.valueSetter('image', base64 || '');
    }
    if (newDimensions) setImageDimensions(newDimensions);
  }, [setImageBase64, formRenderProps, setGroups, setGateways, setGreasePumps]);

  const uploadRef = React.useRef<Upload>(null);

  const isHeic = useCallback((file: File) =>
    file.type === "image/heic" || file.name.toLowerCase().endsWith(".heic"), []);

  const isSvgFile = useCallback((file: File) =>
    file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg"), []);

  const getSvgDimensionsFromDataUrl = useCallback((dataUrl: string): { width: number; height: number } | null => {
    try {
      const comma = dataUrl.indexOf(",");
      if (comma < 0) return null;
      const payload = dataUrl.slice(comma + 1);
      const isBase64 = dataUrl.includes(";base64,");
      const svgString = isBase64 ? atob(payload) : decodeURIComponent(payload);
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, "image/svg+xml");
      const svg = doc.querySelector("svg");
      if (!svg) return null;
      const w = parseFloat(svg.getAttribute("width") ?? "");
      const h = parseFloat(svg.getAttribute("height") ?? "");
      if (w > 0 && h > 0) return { width: w, height: h };
      const viewBox = svg.getAttribute("viewBox");
      if (viewBox) {
        const parts = viewBox.trim().split(/\s+|,/);
        if (parts.length >= 4) {
          const vw = parseFloat(parts[2]);
          const vh = parseFloat(parts[3]);
          if (vw > 0 && vh > 0) return { width: vw, height: vh };
        }
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const svgToPngDataUrl = useCallback(async (dataUrl: string): Promise<string> => {
    const dimensions = getSvgDimensionsFromDataUrl(dataUrl);
    const width = dimensions?.width ?? 800;
    const height = dimensions?.height ?? 600;
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth > 0 ? img.naturalWidth : width;
        const h = img.naturalHeight > 0 ? img.naturalHeight : height;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas not supported"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => reject(new Error("Could not load SVG for conversion."));
      img.src = dataUrl;
    });
  }, [getSvgDimensionsFromDataUrl]);

  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const maxPayloadBytes = 5 * 1024 * 1024;
  const maxBlobBytes = base64BlobLimit(maxPayloadBytes);

  const handleUploadAdd = useCallback(() => {
    setImageUploadError(null);
  }, []);

  const saveUrlAsBase64 = useCallback(async (files: UploadFileInfo[]) => {
    const fileInfo = files[0];
    if (!fileInfo) return { uid: "" };
    const file = fileInfo.getRawFile?.();
    if (!(file instanceof File)) return { uid: fileInfo.uid };

    if (!file.type.startsWith("image/") && !isHeic(file)) return { uid: fileInfo.uid };

    setIsImageLoading(true);
    try {
      let blob: Blob = file;
      if (isHeic(file)) {
        blob = await HeicTo.heicTo({ blob: file, type: "image/jpeg", quality: 0.9 });
      }
      if (blob.size > maxBlobBytes) {
        blob = await resizeImageToMaxSize(blob, maxBlobBytes);
      }

      let dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Error reading file."));
        reader.onload = () => resolve(String(reader.result));
        reader.readAsDataURL(blob);
      });
      if (isSvgFile(file)) {
        dataUrl = await svgToPngDataUrl(dataUrl);
      }
      const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1]! : dataUrl;

      const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => reject(new Error("Could not load image."));
        img.src = dataUrl;
      });

      setImageUploadError(null);
      handleImageChange(base64, dimensions);
      return { uid: fileInfo.uid };
    } finally {
      setIsImageLoading(false);
    }
  }, [handleImageChange, isHeic, isSvgFile, svgToPngDataUrl, maxBlobBytes]);

  const tileData = [
    {
      body: (
        <div className="h-full w-full flex flex-col min-h-0 gap-2">
          {/* Image: flex-1 - única que ajusta de tamanho */}
          <div className="flex-1 min-h-0 flex flex-col relative">
            <div className="flex justify-between align-middle mb-2">
              <div>
                <div className="text-xl font-bold">{templateName || localizationService.toLanguageString("custom.templateImage", enMessages.custom.templateImage)}</div>
                {imageUrl && (
                  <p className="text-xs text-gray-500 mt-1">{localizationService.toLanguageString("custom.dragGroupsAndGateways", enMessages.custom.dragGroupsAndGateways)}</p>
                )}
                {imageUploadError && (
                  <p className="text-sm text-red-600 mt-1">{imageUploadError}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <ImageUploadButton
                  value={currentImageValue || null}
                  onChange={handleImageChange}
                  accept="image/*"
                  base64ModeOutgoing="raw"
                  base64ModeIncoming="raw"
                  incomingMimeType="image/png"
                  isEditMode={!!currentImageValue}
                  uploadRef={uploadRef}
                  disabled={isImageLoading}
                />
              </div>
            </div>
            <div className="sr-only" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>
              <Upload
                ref={uploadRef}
                batch={false}
                multiple={false}
                defaultFiles={[]}
                showFileList={false}
                showActionButtons={false}
                autoUpload={true}
                accept="image/*,image/heic,image/svg+xml"
                restrictions={{ allowedExtensions: [".jpg", ".jpeg", ".png", ".bmp", ".webp", ".heic", ".svg"] }}
                onAdd={handleUploadAdd}
                saveUrl={saveUrlAsBase64}
                removeUrl={async () => ({ uid: "" })}
                disabled={isImageLoading}
              />
            </div>
            <div className="flex-1 min-h-[150px] min-w-0 relative">
            {imageUrl ? (
              <div className="h-full min-h-[150px] overflow-auto">
                <AssetImageComponent
                  key={imageVersion}
                  assetData={createAssetData(
                    templateName || localizationService.toLanguageString("custom.templateImage", enMessages.custom.templateImage),
                    groups,
                    endpoints,
                    gateways
                  )}
                  selectedGroupId={selectedGroupId}
                  onMarkerSelect={handleGroupSelect}
                  assetImageUrl={imageUrl}
                  onMarkersChange={handleMarkersChange}
                  onGatewayMarkersChange={handleGatewayMarkersChange}
                  templateName={templateName || localizationService.toLanguageString("custom.templateImage", enMessages.custom.templateImage)}
                  onEditStateChange={setIsGroupImageEditing}
                  mode="groups"
                  alwaysEditMode={true}
                  instructionText={localizationService.toLanguageString("custom.dragGroupsAndGateways", enMessages.custom.dragGroupsAndGateways)}
                  hideHeader={true}
                  hideDisabledOrDeleted={true}
                />
              </div>
            ) : (
              <div className="template-dropzone-wrapper absolute inset-0 min-h-[150px]">
                <style>{`
                  .template-dropzone-wrapper .k-external-dropzone {
                    position: absolute !important;
                    top: 0 !important;
                    left: 0 !important;
                    right: 0 !important;
                    bottom: 0 !important;
                    width: 100% !important;
                    height: 100% !important;
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    justify-content: center !important;
                  }
                  .template-dropzone-wrapper .k-external-dropzone .k-dropzone-inner {
                    display: flex !important;
                    flex: 1 !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    justify-content: center !important;
                    width: 100% !important;
                    height: 100% !important;
                  }
                `}</style>
                <ExternalDropZone
                  uploadRef={uploadRef}
                  className="template-dropzone-full"
                  customHint={
                    <span className="text-gray-500 text-center">
                      {localizationService.toLanguageString("custom.dragAndDropImage", enMessages.custom.dragAndDropImage)}
                    </span>
                  }
                  customNote={
                    <span className="text-gray-500 text-center">
                      (.jpg, .jpeg, .png, .bmp, .webp, .heic, .svg)
                    </span>
                  }
                />
              </div>
            )}
            {isImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                <Loader type="pulsing" />
              </div>
            )}
            </div>
          </div>
          <div className="flex-shrink-0 flex flex-col" style={{ height: 190 }}>
            <GatewaysTableTemplateComponent
              gateways={gateways}
              onGatewaysChange={handleGatewaysChange}
              onAddGateway={handleAddGateway}
              isEditTemplate={isEditMode}
              instructionText={localizationService.toLanguageString("custom.addOrEditGateways", enMessages.custom.addOrEditGateways)}
            />
          </div>
        </div>
      ),
      reorderable: false,
    },
    {
      body: (
        <div className="h-full flex flex-col gap-4 overflow-hidden">
          <div className="flex-1 min-h-0">
            <GroupsTableTemplateComponent
              groups={groups}
              onGroupsChange={handleGroupsTableChange}
              onGroupSelect={handleGroupSelect}
              selectedGroupId={selectedGroupId}
              endpoints={endpoints}
              onEndpointsChange={setEndpoints}
              onAddGroup={handleAddGroup}
              isEditTemplate={isEditMode}
              instructionText={localizationService.toLanguageString("custom.addOrEditGroupsInstruction", enMessages.custom.addOrEditGroupsInstruction)}
            />
          </div>
          <div className="flex-1 min-h-0 overflow-auto overscroll-contain">
            <EndpointsTableTemplateComponent
              assetData={createAssetData(
                templateName || '',
                groups,
                endpoints,
                gateways
              )}
              selectedGroupId={selectedGroupId}
              endpoints={endpoints}
              onEndpointsChange={setEndpoints}
              onAddEndpoint={handleAddEndpoint}
              canAddEndpoint={groups.length > 0}
              isEditTemplate={isEditMode}
              instructionText={localizationService.toLanguageString("custom.selectGroupToAddEndpoints", enMessages.custom.selectGroupToAddEndpoints)}
            />
          </div>
        </div>
      ),
      reorderable: false,
    },
  ];

  const columnsDisplayPosition = getColumnsDisplayPosition(isSingleColumn);

  return (
    <div className="w-full pt-4 overflow-y-auto overscroll-contain flex flex-col min-h-0" style={{ height: containerHeight }}>
      <TileLayout
        columns={isSingleColumn ? 1 : 2}
        rowHeight={rowHeight}
        positions={columnsDisplayPosition}
        gap={{ rows: 10, columns: 10 }}
        items={tileData}
        className="w-full dashboard-page-tile flex-1 min-h-0"
        style={{
          width: '100%',
          height: isSingleColumn ? 'auto' : '100%'
        }}
      />
    </div>
  );
}
